/**
 * ExplainerCard — progressive-disclosure shell for CSK explainers.
 *
 * Wraps the shared `Card` primitive with a fixed five-tier tab strip:
 * TL;DR → Intuition → Formal → CSK relevance → References.
 *
 * Why a flat tab strip rather than nested accordions?
 *
 * - Tabs stay one tap away on mobile and one keypress away on desktop.
 * - The tier order is pedagogical — the reader's eye should travel left
 *   (most accessible) to right (most formal). Accordions would let a
 *   reader open every tier at once and overwhelm the page.
 * - Tab focus management is a solved problem and screen readers handle it
 *   well via `role="tablist"` plus the roving-tabindex pattern.
 *
 * Accessibility:
 *
 * - Roving `tabIndex`: only the active tab is in the tab sequence; others
 *   are reachable via ArrowLeft/ArrowRight/Home/End.
 * - Only the active tier renders a `tabpanel`, and all tabs point
 *   `aria-controls` to that one stable panel id. Hiding inactive panels in
 *   the DOM (50 cards × 4 hidden panels = 200 extra wrapper divs in the hub)
 *   is measurable under v8 coverage; activating an inactive tab swaps the
 *   panel content in place while preserving valid ARIA wiring.
 *
 * The `diagram` slot renders **above** the active tier so a curious reader
 * sees the picture even at the TL;DR level, before any prose gates them.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useId, useRef, useState } from "react";
import { Card } from "../../components/Card.js";
import { SafeLink } from "../../components/SafeLink.js";
import { FONT, GRAY, withAlpha } from "../../design-system/tokens.js";
import type {
  ExplainerCrossLink,
  ExplainerIdentity,
  ExplainerReference,
  ExplainerTier,
  ExplainerTierContent,
} from "./ExplainerTypes.js";

// inline: local
const TIER_ORDER: readonly { id: ExplainerTier; label: string; hint: string }[] = [
  { id: "tldr", label: "TL;DR", hint: "One sentence. No notation." },
  { id: "intuition", label: "Intuition", hint: "Plain English, with an analogy." },
  { id: "formal", label: "Formal", hint: "Categorical definition and types." },
  { id: "relevance", label: "CSK relevance", hint: "What attack class this neutralizes." },
  { id: "references", label: "References", hint: "Repo paths and external sources." },
] as const;

// Hoisted style constants. Re-creating these objects on every render of every
// card (50+ cards in the hub) shows up as measurable React-DOM reconciliation
// overhead under the v8 coverage instrumentation used by CI; allocating once
// here lets React skip prop-diffing on the style attribute.
const CARD_OUTER_STYLE: React.CSSProperties = { marginBottom: "1rem" };

const HEADER_WRAPPER_STYLE: React.CSSProperties = { marginBottom: "0.5rem" };

const EYEBROW_STYLE: React.CSSProperties = {
  fontSize: "0.6875rem",
  fontWeight: 700,
  color: GRAY[500],
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const HEADING_STYLE: React.CSSProperties = {
  margin: "0.125rem 0 0",
  fontSize: "1.25rem",
  color: GRAY[900],
  fontFamily: FONT.heading,
  display: "flex",
  alignItems: "baseline",
  gap: "0.5rem",
  flexWrap: "wrap",
};

const SUBTITLE_STYLE: React.CSSProperties = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: GRAY[600],
  fontFamily: FONT.mono,
};

const TABLIST_STYLE: React.CSSProperties = {
  display: "flex",
  gap: "0.25rem",
  borderBottom: `1px solid ${GRAY[300]}`,
  flexWrap: "wrap",
};

const ACTIVE_PANEL_STYLE: React.CSSProperties = {
  padding: "0.875rem 0.25rem 0.5rem",
  color: GRAY[700],
  fontSize: "0.9375rem",
  lineHeight: 1.55,
};

const CROSSLINKS_WRAPPER_STYLE: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.375rem",
  paddingTop: "0.5rem",
  borderTop: `1px dashed ${GRAY[300]}`,
  marginTop: "0.5rem",
};

const CROSSLINKS_LABEL_STYLE: React.CSSProperties = {
  fontSize: "0.6875rem",
  color: GRAY[500],
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginRight: "0.25rem",
  alignSelf: "center",
};

const INACTIVE_TAB_STYLE: React.CSSProperties = {
  padding: "0.4rem 0.75rem",
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: GRAY[600],
  background: "transparent",
  border: "none",
  borderBottom: "2px solid transparent",
  cursor: "pointer",
  fontFamily: FONT.body,
};

const EMPTY_REFERENCES_STYLE: React.CSSProperties = { color: GRAY[600], margin: 0 };

const REFERENCE_LIST_STYLE: React.CSSProperties = { margin: 0, paddingLeft: "1.25rem" };

const REFERENCE_ITEM_STYLE: React.CSSProperties = { marginBottom: "0.375rem" };

const REFERENCE_LABEL_STYLE: React.CSSProperties = { fontWeight: 600, color: GRAY[900] };

const REFERENCE_PATH_LINK_STYLE: React.CSSProperties = {
  fontFamily: FONT.mono,
  fontSize: "0.8125rem",
  color: GRAY[700],
  textDecoration: "underline",
  wordBreak: "break-all",
};

const REFERENCE_CODE_STYLE: React.CSSProperties = {
  fontFamily: FONT.mono,
  fontSize: "0.8125rem",
  backgroundColor: GRAY[100],
  color: GRAY[700],
  padding: "0.05rem 0.3rem",
  borderRadius: "0.2rem",
};

const REFERENCE_NOTE_STYLE: React.CSSProperties = {
  color: GRAY[600],
  fontSize: "0.8125rem",
  marginTop: "0.125rem",
};

const ACTIVE_TAB_STYLE_BY_ACCENT = new Map<string, React.CSSProperties>();
const DIAGRAM_FRAME_STYLE_BY_ACCENT = new Map<string, React.CSSProperties>();
const CROSSLINK_STYLE_BY_ACCENT = new Map<string, React.CSSProperties>();

function activeTabStyle(accent: string): React.CSSProperties {
  const cached = ACTIVE_TAB_STYLE_BY_ACCENT.get(accent);
  if (cached !== undefined) return cached;
  const style: React.CSSProperties = {
    ...INACTIVE_TAB_STYLE,
    fontWeight: 600,
    color: accent,
    borderBottom: `2px solid ${accent}`,
  };
  ACTIVE_TAB_STYLE_BY_ACCENT.set(accent, style);
  return style;
}

function diagramFrameStyle(accent: string): React.CSSProperties {
  const cached = DIAGRAM_FRAME_STYLE_BY_ACCENT.get(accent);
  if (cached !== undefined) return cached;
  const style: React.CSSProperties = {
    margin: "0.5rem 0 0.75rem",
    padding: "0.75rem",
    backgroundColor: withAlpha(accent, "0D"),
    border: `1px solid ${withAlpha(accent, "33")}`,
    borderRadius: "0.5rem",
  };
  DIAGRAM_FRAME_STYLE_BY_ACCENT.set(accent, style);
  return style;
}

function crossLinkStyle(accent: string | undefined): React.CSSProperties {
  const backgroundSource = accent ?? GRAY[600];
  const color = accent ?? GRAY[700];
  const cacheKey = `${backgroundSource}:${color}`;
  const cached = CROSSLINK_STYLE_BY_ACCENT.get(cacheKey);
  if (cached !== undefined) return cached;
  const style: React.CSSProperties = {
    fontSize: "0.75rem",
    padding: "0.2rem 0.55rem",
    borderRadius: "9999px",
    backgroundColor: withAlpha(backgroundSource, "1A"),
    color,
    textDecoration: "none",
    fontWeight: 600,
  };
  CROSSLINK_STYLE_BY_ACCENT.set(cacheKey, style);
  return style;
}

function isUrl(path: string): boolean {
  return /^https?:\/\//i.test(path);
}

export interface ExplainerCardProps {
  readonly identity: ExplainerIdentity;
  readonly tiers: ExplainerTierContent;
  /** Optional inline-SVG diagram; rendered above the active tier. */
  readonly diagram?: React.ReactNode;
  /** Optional cross-links rendered as chips beneath the tier strip. */
  readonly crossLinks?: readonly ExplainerCrossLink[];
  /** Initial tier to display (defaults to `tldr`). */
  readonly initialTier?: ExplainerTier;
  /** Optional override callback when a tier is selected (for instrumentation). */
  readonly onTierChange?: (tier: ExplainerTier) => void;
}

export const ExplainerCard = React.memo(function ExplainerCard({
  identity,
  tiers,
  diagram,
  crossLinks,
  initialTier = "tldr",
  onTierChange,
}: ExplainerCardProps): React.JSX.Element {
  const [active, setActive] = useState<ExplainerTier>(initialTier);
  const reactId = useId();
  const tabRefs = useRef<Map<ExplainerTier, HTMLButtonElement | null>>(new Map());

  const select = useCallback(
    (tier: ExplainerTier) => {
      setActive(tier);
      onTierChange?.(tier);
    },
    [onTierChange],
  );

  const focusTab = useCallback((tier: ExplainerTier) => {
    tabRefs.current.get(tier)?.focus();
  }, []);

  const onTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, tier: ExplainerTier) => {
      const index = TIER_ORDER.findIndex((t) => t.id === tier);
      if (index < 0) return;
      let next: ExplainerTier | undefined;
      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          next = TIER_ORDER[(index + 1) % TIER_ORDER.length]?.id;
          break;
        case "ArrowLeft":
        case "ArrowUp":
          next = TIER_ORDER[(index - 1 + TIER_ORDER.length) % TIER_ORDER.length]?.id;
          break;
        case "Home":
          next = TIER_ORDER[0]?.id;
          break;
        case "End":
          next = TIER_ORDER[TIER_ORDER.length - 1]?.id;
          break;
        default:
          return;
      }
      if (next === undefined) return;
      event.preventDefault();
      select(next);
      focusTab(next);
    },
    [focusTab, select],
  );

  const accent = identity.accent;
  const ariaLabel = `${identity.kind}: ${identity.title}`;
  const tablistLabel = `${identity.title} tiers`;
  const panelId = `${reactId}-panel`;

  return (
    <Card accent={accent} id={identity.id} aria-label={ariaLabel} style={CARD_OUTER_STYLE}>
      <header style={HEADER_WRAPPER_STYLE}>
        <div style={EYEBROW_STYLE}>{identity.kind}</div>
        <h3 style={HEADING_STYLE}>
          <span>{identity.title}</span>
          {identity.subtitle !== undefined && (
            <span style={SUBTITLE_STYLE}>{identity.subtitle}</span>
          )}
        </h3>
      </header>

      {diagram !== undefined && <DiagramFrame accent={accent}>{diagram}</DiagramFrame>}

      <div role="tablist" aria-label={tablistLabel} style={TABLIST_STYLE}>
        {TIER_ORDER.map((tier) => (
          <TabButton
            key={tier.id}
            tier={tier}
            isActive={tier.id === active}
            reactId={reactId}
            panelId={panelId}
            accent={accent}
            registerRef={tabRefs}
            onSelect={select}
            onTabKeyDown={onTabKeyDown}
          />
        ))}
      </div>

      <div
        role="tabpanel"
        id={panelId}
        aria-labelledby={`${reactId}-${active}`}
        style={ACTIVE_PANEL_STYLE}
      >
        {active === "references" ? <ReferenceList items={tiers.references} /> : tiers[active]}
      </div>

      {crossLinks !== undefined && crossLinks.length > 0 && (
        <div style={CROSSLINKS_WRAPPER_STYLE} aria-label="Related explainers">
          <span style={CROSSLINKS_LABEL_STYLE}>See also</span>
          {crossLinks.map((link) => (
            <CrossLinkChip key={`${link.label}:${link.href}`} link={link} />
          ))}
        </div>
      )}
    </Card>
  );
});

interface DiagramFrameProps {
  readonly accent: string;
  readonly children: React.ReactNode;
}

const DiagramFrame = React.memo(function DiagramFrame({
  accent,
  children,
}: DiagramFrameProps): React.JSX.Element {
  return <div style={diagramFrameStyle(accent)}>{children}</div>;
});

interface TabButtonProps {
  readonly tier: (typeof TIER_ORDER)[number];
  readonly isActive: boolean;
  readonly reactId: string;
  readonly panelId: string;
  readonly accent: string;
  readonly registerRef: React.RefObject<Map<ExplainerTier, HTMLButtonElement | null>>;
  readonly onSelect: (tier: ExplainerTier) => void;
  readonly onTabKeyDown: (
    event: React.KeyboardEvent<HTMLButtonElement>,
    tier: ExplainerTier,
  ) => void;
}

const TabButton = React.memo(function TabButton({
  tier,
  isActive,
  reactId,
  panelId,
  accent,
  registerRef,
  onSelect,
  onTabKeyDown,
}: TabButtonProps): React.JSX.Element {
  const tabId = `${reactId}-${tier.id}`;
  return (
    <button
      ref={(el) => {
        if (el === null) {
          registerRef.current.delete(tier.id);
        } else {
          registerRef.current.set(tier.id, el);
        }
      }}
      type="button"
      role="tab"
      id={tabId}
      aria-selected={isActive}
      aria-controls={panelId}
      tabIndex={isActive ? 0 : -1}
      onClick={() => {
        onSelect(tier.id);
      }}
      onKeyDown={(event) => {
        onTabKeyDown(event, tier.id);
      }}
      title={tier.hint}
      style={isActive ? activeTabStyle(accent) : INACTIVE_TAB_STYLE}
    >
      {tier.label}
    </button>
  );
});

const CrossLinkChip = React.memo(function CrossLinkChip({
  link,
}: {
  readonly link: ExplainerCrossLink;
}): React.JSX.Element {
  return (
    <a href={link.href} style={crossLinkStyle(link.accent)}>
      {link.label}
    </a>
  );
});

function ReferenceList({
  items,
}: {
  readonly items: readonly ExplainerReference[];
}): React.JSX.Element {
  if (items.length === 0) {
    return <p style={EMPTY_REFERENCES_STYLE}>No references registered.</p>;
  }
  return (
    <ul style={REFERENCE_LIST_STYLE}>
      {items.map((ref) => (
        <li key={`${ref.label}:${ref.path}`} style={REFERENCE_ITEM_STYLE}>
          <span style={REFERENCE_LABEL_STYLE}>{ref.label}</span>{" "}
          {isUrl(ref.path) ? (
            <SafeLink href={ref.path} style={REFERENCE_PATH_LINK_STYLE}>
              {ref.path}
            </SafeLink>
          ) : (
            <code style={REFERENCE_CODE_STYLE}>{ref.path}</code>
          )}
          {ref.note !== undefined && <div style={REFERENCE_NOTE_STYLE}>{ref.note}</div>}
        </li>
      ))}
    </ul>
  );
}
